import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import { Category, Project } from "../types";
import { apiClient } from "../utils/apiClient";

interface ProjectFormData {
  name: string;
  category_id: string | number;
  active: boolean;
  description: string;
}

const ProjectMaintenance = () => {
  const API_PREFIX = "/api";

  const [projects, setProjects] = useState<Project[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);

  // メッセージ表示用ステート
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // 新規登録フォーム用ステート
  const [newFormData, setNewFormData] = useState<ProjectFormData>({
    name: "",
    category_id: "",
    active: true,
    description: "",
  });

  // フィルタ用ステート
  const [filterCategoryId, setFilterCategoryId] = useState<string>("");
  const [filterActiveStatus, setFilterActiveStatus] = useState<string>("all");

  // 行内編集用ステート
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editCategoryId, setEditCategoryId] = useState<number | string>("");
  const [editActive, setEditActive] = useState(true);
  const [editDescription, setEditDescription] = useState("");

  // データ読み込み
  useEffect(() => {
    fetchProjects();
    fetchCategories();
  }, []);

  const fetchProjects = () => {
    apiClient
      .get<Project[]>(`${API_PREFIX}/projects`)
      .then((data) => setProjects(data))
      .catch((err) => {
        console.error("Projects fetch error:", err);
        setErrorMessage("プロジェクトの取得に失敗しました");
      });
  };

  const fetchCategories = () => {
    apiClient
      .get<Category[]>(`${API_PREFIX}/categories`)
      .then((data) => setCategories(data))
      .catch((err) => {
        console.error("Categories fetch error:", err);
        setErrorMessage("カテゴリの取得に失敗しました");
      });
  };

  // メッセージリセット用ヘルパー
  const resetMessages = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  // --- フィルタリングロジック ---
  const filteredProjects = projects.filter((p) => {
    const matchesCategory =
      filterCategoryId === "" || String(p.category_id) === filterCategoryId;
    const matchesStatus =
      filterActiveStatus === "all"
        ? true
        : filterActiveStatus === "active"
          ? p.active === true
          : p.active === false;
    return matchesCategory && matchesStatus;
  });

  // --- 新規登録処理 ---
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFormData.name || !newFormData.category_id) {
      setErrorMessage("プロジェクト名とカテゴリは必須です");
      return;
    }
    resetMessages();
    try {
      await apiClient.post(`${API_PREFIX}/projects`, { project: newFormData });
      setSuccessMessage("登録しました");
      setNewFormData({ name: "", category_id: "", active: true, description: "" });
      fetchProjects();
    } catch (err: any) {
      setErrorMessage(err.message || "登録に失敗しました");
    }
  };

  // --- 行内編集コントロール ---
  const startEdit = (project: Project) => {
    resetMessages();
    setEditingId(project.id);
    setEditName(project.name);
    setEditCategoryId(project.category_id);
    setEditActive(project.active);
    setEditDescription(project.description || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    resetMessages();
  };

  const handleUpdate = async (id: number) => {
    if (!editName || !editCategoryId) {
      setErrorMessage("プロジェクト名とカテゴリは必須です");
      return;
    }
    resetMessages();
    try {
      await apiClient.put(`${API_PREFIX}/projects/${id}`, {
        project: {
          name: editName,
          category_id: editCategoryId,
          active: editActive,
          description: editDescription,
        },
      });
      setEditingId(null);
      setSuccessMessage("更新しました");
      fetchProjects();
    } catch (err: any) {
      setErrorMessage(err.message || "更新に失敗しました");
    }
  };

  const handleDelete = async (id: number) => {
    if (
      !window.confirm(
        "本当に削除しますか？\n（使用済みのプロジェクトは削除ではなく「無効」にしてください）",
      )
    )
      return;
    resetMessages();
    try {
      await apiClient.delete(`${API_PREFIX}/projects/${id}`);
      setSuccessMessage("削除しました");
      fetchProjects();
    } catch (err: any) {
      setErrorMessage(
        "削除できませんでした。関連データがある可能性があります。",
      );
    }
  };

  return (
    <div
      className="container-fluid p-0"
      style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}
    >
      <Header title="プロジェクト管理" />

      <div className="limit-width px-4">
        <div className="mb-4">
          <Link
            to="/admin"
            className="text-decoration-none text-secondary d-inline-flex align-items-center"
          >
            <span className="fs-4 me-2">‹</span> 管理者メニューに戻る
          </Link>
        </div>

        {successMessage && (
          <div className="alert alert-success mb-4">✅ {successMessage}</div>
        )}
        {errorMessage && (
          <div
            className="alert alert-danger mb-4"
            style={{ whiteSpace: "pre-line" }}
          >
            ⚠️ {errorMessage}
          </div>
        )}

        {/* 新規登録フォーム */}
        <div className="card mb-4 shadow-sm border-0 bg-light">
          <div className="card-body">
            <div className="d-flex align-items-end gap-3 mb-3">
              <div className="flex-grow-1">
                <label className="form-label small fw-bold">カテゴリ</label>
                <select
                  className="form-select"
                  value={newFormData.category_id}
                  onChange={(e) =>
                    setNewFormData({
                      ...newFormData,
                      category_id: e.target.value,
                    })
                  }
                >
                  <option value="">選択してください</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-grow-1">
                <label className="form-label small fw-bold">
                  プロジェクト名
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="新規追加するプロジェクト名を入力"
                  value={newFormData.name}
                  onChange={(e) =>
                    setNewFormData({ ...newFormData, name: e.target.value })
                  }
                />
              </div>
              <div style={{ width: "80px" }}>
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="newActiveCheck"
                    checked={newFormData.active}
                    onChange={(e) =>
                      setNewFormData({
                        ...newFormData,
                        active: e.target.checked,
                      })
                    }
                  />
                  <label className="form-check-label" htmlFor="newActiveCheck">
                    有効
                  </label>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-primary fw-bold px-4"
                onClick={handleCreate}
              >
                追加
              </button>
            </div>
            <div>
              <label className="form-label small fw-bold">
                説明（業務報告登録時にプルダウンで表示されます・任意）
              </label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="プロジェクトの説明を入力"
                value={newFormData.description}
                onChange={(e) =>
                  setNewFormData({
                    ...newFormData,
                    description: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>

        {/* フィルタリングUI */}
        <div className="d-flex justify-content-between align-items-center mb-3 bg-white p-3 rounded shadow-sm border">
          <div className="d-flex gap-3 align-items-center">
            <span className="small fw-bold text-secondary text-nowrap">
              絞り込み:
            </span>
            <select
              className="form-select form-select-sm"
              style={{ width: "200px" }}
              value={filterCategoryId}
              onChange={(e) => setFilterCategoryId(e.target.value)}
            >
              <option value="">すべてのカテゴリ</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              style={{ width: "150px" }}
              value={filterActiveStatus}
              onChange={(e) => setFilterActiveStatus(e.target.value)}
            >
              <option value="all">すべての状態</option>
              <option value="active">有効のみ</option>
              <option value="inactive">無効のみ</option>
            </select>
          </div>
          <div className="text-secondary small">
            該当件数: <strong>{filteredProjects.length}</strong> 件
          </div>
        </div>

        {/* 一覧テーブル */}
        <div className="card shadow-sm border-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th style={{ width: "5%" }}>ID</th>
                  <th style={{ width: "8%" }}>状態</th>
                  <th style={{ width: "15%" }}>カテゴリ</th>
                  <th style={{ width: "22%" }}>プロジェクト名</th>
                  <th style={{ width: "30%" }}>説明</th>
                  <th style={{ width: "20%" }} className="text-center">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => (
                  <tr
                    key={project.id}
                    className={!project.active ? "text-muted bg-light" : ""}
                  >
                    {editingId === project.id ? (
                      <>
                        <td>{project.id}</td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={editActive}
                              onChange={(e) => setEditActive(e.target.checked)}
                            />
                            <span
                              className={`badge ${editActive ? "bg-success" : "bg-secondary"}`}
                            >
                              {editActive ? "有効" : "無効"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={editCategoryId}
                            onChange={(e) => setEditCategoryId(e.target.value)}
                          >
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                          />
                        </td>
                        <td>
                          <textarea
                            className="form-control form-control-sm"
                            rows={2}
                            value={editDescription}
                            onChange={(e) =>
                              setEditDescription(e.target.value)
                            }
                          />
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-success btn-sm me-2"
                            onClick={() => handleUpdate(project.id)}
                          >
                            保存
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={cancelEdit}
                          >
                            中止
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{project.id}</td>
                        <td>
                          <span
                            className={`badge ${project.active ? "bg-success" : "bg-secondary"}`}
                          >
                            {project.active ? "有効" : "無効"}
                          </span>
                        </td>
                        <td>{project.category?.name}</td>
                        <td className="fw-bold">{project.name}</td>
                        <td
                          className="text-muted small"
                          style={{
                            whiteSpace: "pre-line",
                            wordBreak: "break-word",
                          }}
                        >
                          {project.description || "—"}
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => startEdit(project)}
                          >
                            編集
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(project.id)}
                          >
                            削除
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectMaintenance;
